/**
 * Laboratory API client — every call goes through lib/api/client.ts, so error
 * codes, the branch header and ETag concurrency are handled once (see that
 * file's header) rather than per screen.
 *
 * Nothing here computes a flag, a reference range or a status transition. All
 * three come back from the server already decided; a client that decided them
 * itself could be made to decide differently, and the numbers a laboratory
 * publishes are exactly the wrong place for that.
 */
import { api, unwrap, type Paged } from '@/lib/api/client';
import { getAuthToken, getActiveBranchId } from '@/lib/api/session';
import type {
  CreateLabOrderInput, CriticalResult, LabContainer, LabDashboard, LabDepartment,
  LabOrder, LabOrderDetail, LabPackage, LabParameter, LabReferenceRange, LabReport,
  LabReportBundle, LabReportVersion, LabSample, LabSampleType, LabTest, LabTestCategory,
  LabTestDetail, PatientLabOrder, RejectionReason, RejectionReportRow, ResultQueueRow,
  ResultSheet, RevenueReportRow, SaveResultInput, TatReportRow, TestWiseReportRow, TrendPoint,
  OcrResult, OutsourcedDetail, OutsourcedQueue, OutsourcedRow, PatientOutsourcedTest, ReferenceLab, TranscriptionInput,
} from './types';

const BASE = '/laboratory';

// ── Dashboard & MIS ──────────────────────────────────────────────

export function fetchLabDashboard(): Promise<LabDashboard> {
  return api.get(`${BASE}/dashboard`);
}

export function fetchTatReport(from?: string, to?: string): Promise<TatReportRow[]> {
  return api.get(`${BASE}/reports/tat`, { query: { from, to } });
}
export function fetchTestWiseReport(from?: string, to?: string): Promise<TestWiseReportRow[]> {
  return api.get(`${BASE}/reports/test-wise`, { query: { from, to } });
}
export function fetchLabRevenueReport(from?: string, to?: string): Promise<RevenueReportRow[]> {
  return api.get(`${BASE}/reports/revenue`, { query: { from, to } });
}
export function fetchRejectionReport(from?: string, to?: string): Promise<RejectionReportRow[]> {
  return api.get(`${BASE}/reports/rejections`, { query: { from, to } });
}

// ── Masters ──────────────────────────────────────────────────────

export const fetchDepartments = (includeInactive = false): Promise<LabDepartment[]> =>
  api.get(`${BASE}/departments`, { query: { include_inactive: includeInactive ? 1 : undefined } });
export const fetchTestCategories = (): Promise<LabTestCategory[]> => api.get(`${BASE}/test-categories`);
export const fetchSampleTypes = (): Promise<LabSampleType[]> => api.get(`${BASE}/sample-types`);
export const fetchContainers = (): Promise<LabContainer[]> => api.get(`${BASE}/containers`);

export const createDepartment = (b: Partial<LabDepartment>) => api.post<LabDepartment>(`${BASE}/departments`, b);
export const updateDepartment = (id: number, b: Partial<LabDepartment>) => api.put<LabDepartment>(`${BASE}/departments/${id}`, b);
export const createTestCategory = (b: Partial<LabTestCategory>) => api.post<LabTestCategory>(`${BASE}/test-categories`, b);
export const updateTestCategory = (id: number, b: Partial<LabTestCategory>) => api.put<LabTestCategory>(`${BASE}/test-categories/${id}`, b);
export const createSampleType = (b: Partial<LabSampleType>) => api.post<LabSampleType>(`${BASE}/sample-types`, b);
export const updateSampleType = (id: number, b: Partial<LabSampleType>) => api.put<LabSampleType>(`${BASE}/sample-types/${id}`, b);
export const createContainer = (b: Partial<LabContainer>) => api.post<LabContainer>(`${BASE}/containers`, b);
export const updateContainer = (id: number, b: Partial<LabContainer>) => api.put<LabContainer>(`${BASE}/containers/${id}`, b);

export async function fetchTests(params: { q?: string; department_id?: number; include_inactive?: boolean; processing_type?: string } = {}): Promise<LabTest[]> {
  const res = await api.get<LabTest[] | Paged<LabTest>>(`${BASE}/tests`, {
    query: {
      q: params.q, department_id: params.department_id, processing_type: params.processing_type,
      include_inactive: params.include_inactive ? 1 : undefined,
      page: 1, limit: 200,
    },
  });
  return unwrap(res);
}

export const fetchTest = (id: number): Promise<LabTestDetail> => api.get(`${BASE}/tests/${id}`);
export const createTest = (b: Partial<LabTest>) => api.post<LabTest>(`${BASE}/tests`, b);
export const updateTest = (id: number, b: Partial<LabTest>) => api.put<LabTest>(`${BASE}/tests/${id}`, b);

export const createParameter = (testId: number, b: Partial<LabParameter>) =>
  api.post<LabParameter>(`${BASE}/tests/${testId}/parameters`, b);
export const updateParameter = (id: number, b: Partial<LabParameter>) =>
  api.put<LabParameter>(`${BASE}/parameters/${id}`, b);

export const fetchReferenceRanges = (parameterId: number): Promise<LabReferenceRange[]> =>
  api.get(`${BASE}/parameters/${parameterId}/reference-ranges`);
export const createReferenceRange = (parameterId: number, b: Partial<LabReferenceRange>) =>
  api.post<LabReferenceRange>(`${BASE}/parameters/${parameterId}/reference-ranges`, b);
export const updateReferenceRange = (id: number, b: Partial<LabReferenceRange>) =>
  api.put<LabReferenceRange>(`${BASE}/reference-ranges/${id}`, b);

export const fetchPackages = (): Promise<LabPackage[]> => api.get(`${BASE}/packages`);
/**
 * A package is created from test IDS — the caller has never seen the joined
 * test_name/test_code that a fetched LabPackage carries, so the input type is
 * its own rather than Partial<LabPackage>, whose `tests` would demand columns
 * only a read produces.
 */
export interface SaveLabPackageInput {
  code?: string; name?: string; description?: string | null;
  price?: string | number; gst_percent?: string | number; hsn_code?: string | null;
  active?: boolean;
  tests: Array<{ test_id: number }>;
}

export const createPackage = (b: SaveLabPackageInput) =>
  api.post<LabPackage>(`${BASE}/packages`, b);
export const updatePackage = (id: number, b: Partial<LabPackage>) => api.put<LabPackage>(`${BASE}/packages/${id}`, b);

// ── Orders ───────────────────────────────────────────────────────

export interface LabOrderFilters {
  status?: string;
  priority?: string;
  patient_id?: number;
  doctor_id?: number;
  order_number?: string;
  department_id?: number;
  test_id?: number;
  from?: string;
  to?: string;
  q?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/** Always paged — this list is a hospital's daily workload, never unbounded. */
export function fetchLabOrders(filters: LabOrderFilters = {}): Promise<Paged<LabOrder>> {
  const { page = 1, limit = 25, ...rest } = filters;
  return api.get(`${BASE}/orders`, { query: { ...rest, page, limit } });
}

export const fetchLabOrder = (id: number): Promise<LabOrderDetail> => api.get(`${BASE}/orders/${id}`);

/**
 * Creating an order optionally raises the charge through the EXISTING billing
 * engine — `create_bill` makes the server write an ordinary `bills` row in the
 * same transaction, with the same OPD invoice series and the same GST rules as
 * every other bill in this app. There is no laboratory invoice.
 */
export const createLabOrder = (input: CreateLabOrderInput) => api.post<LabOrderDetail>(`${BASE}/orders`, input);

export const updateLabOrder = (id: number, b: { priority?: string; clinical_notes?: string; diagnosis?: string }) =>
  api.put<LabOrder>(`${BASE}/orders/${id}`, b);

export const cancelLabOrder = (id: number, reason: string, itemIds?: number[]) =>
  api.post<{ cancelled: number; status: string; order_id: number }>(
    `${BASE}/orders/${id}/cancel`, { reason, item_ids: itemIds }
  );

/** TENANT-WIDE — a patient's laboratory history is visible at every branch. */
export async function fetchPatientLabHistory(uhid: string, limit = 50): Promise<PatientLabOrder[]> {
  const res = await api.get<PatientLabOrder[] | Paged<PatientLabOrder>>(`${BASE}/orders/patient/${uhid}`, {
    query: { page: 1, limit },
  });
  return unwrap(res);
}

/** One parameter's verified values over time — the trend comparison. */
export const fetchParameterTrend = (uhid: string, parameterCode: string): Promise<TrendPoint[]> =>
  api.get(`${BASE}/orders/patient/${uhid}/trend`, { query: { parameter_code: parameterCode } });

// ── Samples ──────────────────────────────────────────────────────

export interface LabSampleFilters {
  status?: string; barcode?: string; order_id?: number; patient_id?: number;
  priority?: string; from?: string; to?: string; q?: string;
  page?: number; limit?: number;
}

export function fetchLabSamples(filters: LabSampleFilters = {}): Promise<Paged<LabSample>> {
  const { page = 1, limit = 25, ...rest } = filters;
  return api.get(`${BASE}/samples`, { query: { ...rest, page, limit } });
}

export const fetchLabSample = (id: number): Promise<LabSample> => api.get(`${BASE}/samples/${id}`);

/** The scanner path. A 404 means "not at this branch" and says nothing more. */
export const fetchSampleByBarcode = (barcode: string): Promise<LabSample> =>
  api.get(`${BASE}/samples/barcode/${encodeURIComponent(barcode)}`);

export const collectSample = (id: number, body: { collection_location?: string; remarks?: string }) =>
  api.post<LabSample>(`${BASE}/samples/${id}/collect`, body);

export const receiveSample = (id: number, body: { accept?: boolean; remarks?: string } = {}) =>
  api.post<LabSample>(`${BASE}/samples/${id}/receive`, body);

export const rejectSample = (id: number, body: { reason: RejectionReason; remarks?: string; require_recollection?: boolean }) =>
  api.post<LabSample>(`${BASE}/samples/${id}/reject`, body);

export const recollectSample = (id: number) => api.post<LabSample>(`${BASE}/samples/${id}/recollect`);

export interface SampleLabel {
  barcode: string; order_number: string; patient_name: string; uhid: string;
  sample_type: string | null; container: string | null;
  tests: string[]; collected_at: string | null;
}
export const fetchSampleLabel = (id: number): Promise<SampleLabel> => api.get(`${BASE}/samples/${id}/label`);

// ── Results ──────────────────────────────────────────────────────

export interface ResultQueueFilters {
  queue?: 'pending' | 'entered' | 'verified' | 'reported';
  status?: string; department_id?: number; test_id?: number;
  priority?: string; order_id?: number; patient_id?: number;
  critical?: boolean; from?: string; to?: string; q?: string;
  page?: number; limit?: number;
}

export function fetchResultQueue(filters: ResultQueueFilters = {}): Promise<Paged<ResultQueueRow>> {
  const { page = 1, limit = 25, critical, ...rest } = filters;
  return api.get(`${BASE}/results`, { query: { ...rest, critical: critical ? 1 : undefined, page, limit } });
}

/** `orderItemId`, not a result id — the sheet opens before a result exists. */
export const fetchResultSheet = (orderItemId: number): Promise<ResultSheet> =>
  api.get(`${BASE}/results/${orderItemId}`);

export const saveResult = (orderItemId: number, input: SaveResultInput): Promise<ResultSheet> =>
  api.post(`${BASE}/results/${orderItemId}`, input);

export const verifyResult = (orderItemId: number, body: { decision: 'verified' | 'sent_back'; comments?: string }) =>
  api.post<{ result_id: number; decision: string; item_status: string; order_status: string; order_id: number }>(
    `${BASE}/results/${orderItemId}/verify`, body
  );

/** Reopening a verified/reported result for correction. Needs `lab:amend`. */
export const reopenResult = (orderItemId: number, reason: string): Promise<ResultSheet> =>
  api.post(`${BASE}/results/${orderItemId}/reopen`, { reason });

export const addResultComment = (orderItemId: number, comment: string, commentType: 'technical' | 'interpretive' = 'technical') =>
  api.post(`${BASE}/results/${orderItemId}/comments`, { comment, comment_type: commentType });

// ── Critical results ─────────────────────────────────────────────

export async function fetchCriticalResults(params: { outstanding?: boolean; status?: string; limit?: number } = {}): Promise<CriticalResult[]> {
  const res = await api.get<CriticalResult[] | Paged<CriticalResult>>(`${BASE}/results/critical`, {
    query: {
      outstanding: params.outstanding ? 1 : undefined,
      status: params.status,
      page: 1, limit: params.limit ?? 50,
    },
  });
  return unwrap(res);
}

export const acknowledgeCritical = (id: number, notes?: string) =>
  api.post<CriticalResult>(`${BASE}/results/critical/${id}/acknowledge`, { notes });

// ── Reports ──────────────────────────────────────────────────────

export const fetchLabReport = (orderId: number): Promise<LabReportBundle> => api.get(`${BASE}/reports/${orderId}`);

export const releaseLabReport = (orderId: number) =>
  api.post<{ report: LabReport; version: LabReportVersion }>(`${BASE}/reports/${orderId}/release`);

export const amendLabReport = (orderId: number, reason: string, changedFields?: unknown) =>
  api.post<{ report: LabReport; version: LabReportVersion }>(
    `${BASE}/reports/${orderId}/amend`, { reason, changed_fields: changedFields }
  );

/**
 * Fetch-then-blob rather than a plain <a href>, the same reasoning
 * features/billing/api.ts's openBillPdf documents: a raw navigation sends
 * `Accept: text/html`, which the dev-server proxy does not forward, so the
 * request never reaches the backend. Fetching it ourselves sidesteps that in
 * both dev and production — and, unlike a link, it carries the auth and
 * branch headers, which a report PDF genuinely needs.
 */
// ── Outside / reference laboratory ───────────────────────────────

export const fetchReferenceLabs = (includeInactive = false): Promise<ReferenceLab[]> =>
  api.get(`${BASE}/reference-labs`, { query: { include_inactive: includeInactive ? 1 : undefined } });
export const createReferenceLab = (b: Partial<ReferenceLab>) => api.post<ReferenceLab>(`${BASE}/reference-labs`, b);
export const updateReferenceLab = (id: number, b: Partial<ReferenceLab>) => api.put<ReferenceLab>(`${BASE}/reference-labs/${id}`, b);

export interface OutsourcedFilters {
  queue?: OutsourcedQueue;
  status?: string;
  reference_lab_id?: number;
  test_id?: number;
  doctor_id?: number;
  from?: string; to?: string;
  sent_from?: string; sent_to?: string;
  received_from?: string; received_to?: string;
  /** Patient name, UHID/MRN, mobile, lab order number, accession number or barcode. */
  q?: string;
  page?: number; limit?: number;
}

export function fetchOutsourcedWorklist(filters: OutsourcedFilters = {}): Promise<Paged<OutsourcedRow>> {
  const { page = 1, limit = 25, ...rest } = filters;
  return api.get(`${BASE}/outsourced`, { query: { ...rest, page, limit } });
}

export const fetchOutsourcedItem = (orderItemId: number): Promise<OutsourcedDetail> =>
  api.get(`${BASE}/outsourced/${orderItemId}`);

/** TENANT-WIDE — the patient's outside-laboratory tests at every branch. */
export const fetchPatientOutsourced = (uhid: string): Promise<PatientOutsourcedTest[]> =>
  api.get(`${BASE}/outsourced/patient/${uhid}`);

export const dispatchOutsourcedSample = (sampleId: number, body: {
  reference_lab_id?: number; external_accession_number?: string; expected_report_at?: string; notes?: string;
}) => api.post<LabSample>(`${BASE}/outsourced/samples/${sampleId}/dispatch`, body);

export const changeSampleReferenceLab = (sampleId: number, body: { reference_lab_id: number; reason: string }) =>
  api.put<LabSample>(`${BASE}/outsourced/samples/${sampleId}/reference-lab`, body);

export const updateOutsourcedStatus = (orderItemId: number, body: {
  status?: 'AWAITING_REPORT' | 'REPORT_RECEIVED'; external_accession_number?: string; expected_report_at?: string; notes?: string;
}) => api.post<OutsourcedDetail>(`${BASE}/outsourced/${orderItemId}/status`, body);

export interface AttachReportInput {
  /** A file picked at the desk… */
  file?: File;
  /** …or a document that arrived through the existing QR "scan to upload" session. */
  document_id?: number;
  /** Must equal the order's patient UHID — checked server-side. */
  confirmed_uhid: string;
  report_date?: string;
  replace?: boolean;
  replacement_reason?: string;
  also_item_ids?: number[];
}

/**
 * Multipart when a file is attached, JSON when linking a QR-uploaded document.
 * Hand-rolled fetch (not lib/api/client.ts) only because that client always
 * JSON-encodes; the auth and branch headers are the same ones it sends.
 */
export async function attachOutsourcedReport(orderItemId: number, input: AttachReportInput): Promise<OutsourcedDetail> {
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    const branchId = getActiveBranchId();
    if (branchId !== null) headers['X-Branch-Id'] = String(branchId);
  }
  let body: BodyInit;
  if (input.file) {
    const form = new FormData();
    form.append('file', input.file);
    form.append('confirmed_uhid', input.confirmed_uhid);
    if (input.report_date) form.append('report_date', input.report_date);
    if (input.replace) form.append('replace', 'true');
    if (input.replacement_reason) form.append('replacement_reason', input.replacement_reason);
    if (input.also_item_ids?.length) form.append('also_item_ids', JSON.stringify(input.also_item_ids));
    body = form;
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({ ...input, file: undefined });
  }
  const res = await fetch(`/api${BASE}/outsourced/${orderItemId}/report`, { method: 'POST', headers, body });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data as OutsourcedDetail;
}

export const runOutsourcedOcr = (orderItemId: number) => api.post<OcrResult>(`${BASE}/outsourced/${orderItemId}/ocr`);

export const saveOutsourcedResult = (orderItemId: number, input: TranscriptionInput) =>
  api.put<OutsourcedDetail>(`${BASE}/outsourced/${orderItemId}/result`, input);

export const finalizeOutsourced = (orderItemId: number) =>
  api.post<OutsourcedDetail>(`${BASE}/outsourced/${orderItemId}/finalize`);

/** Opens an attached report (current or superseded) in a new tab; the view is audited server-side. */
export async function openOutsourcedReport(reportId: number): Promise<void> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    const branchId = getActiveBranchId();
    if (branchId !== null) headers['X-Branch-Id'] = String(branchId);
  }
  const res = await fetch(`/api${BASE}/outsourced/reports/${reportId}/file`, { headers });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blobUrl = URL.createObjectURL(await res.blob());
  window.open(blobUrl, '_blank');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}

export async function openLabReportPdf(versionId: number): Promise<void> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    const branchId = getActiveBranchId();
    if (branchId !== null) headers['X-Branch-Id'] = String(branchId);
  }
  const res = await fetch(`/api${BASE}/reports/versions/${versionId}/pdf`, { headers });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blobUrl = URL.createObjectURL(await res.blob());
  window.open(blobUrl, '_blank');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}
